import { DOCUMENT, Location } from '@angular/common';
import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { filter } from 'rxjs';

const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk [^\s]+ failed|ChunkLoadError/i;
const RELOAD_GUARD_KEY = 'solvace.chunk-reload-target';
const LAZY_RELOAD_GUARD_KEY = 'solvace.lazy-chunk-reload-target';

@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _document = inject(DOCUMENT);
  private readonly _destroyRef = inject(DestroyRef);

  init(): void {
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd || event instanceof NavigationError),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this._clearGuard();
        } else if (event instanceof NavigationError) {
          this._recoverFromChunkError(event);
        }
      });
  }

  /**
   * AB#74910 — modal openers fetch their chunk with a plain `import()` at click time, outside the
   * router, so a deploy that replaced the hashed chunks made the click reject silently until the
   * user refreshed. A stale chunk now reloads the page once. Its guard, unlike the router one, is not
   * cleared by the NavigationEnd of that reload, so a chunk still missing afterwards runs `onFailure`
   * instead of reloading on every click; only a lazy chunk that loads clears it.
   */
  async loadLazyChunk<T>(load: () => Promise<T>, onFailure: () => void): Promise<T | null> {
    try {
      const chunk = await load();
      this._document.defaultView?.sessionStorage.removeItem(LAZY_RELOAD_GUARD_KEY);
      return chunk;
    } catch (error) {
      const reloaded = this._isChunkLoadError(error) && this._reloadForLazyChunk();
      if (!reloaded) onFailure();
      return null;
    }
  }

  private _recoverFromChunkError(event: NavigationError): void {
    if (!this._isChunkLoadError(event.error)) return;

    const view = this._document.defaultView;
    if (!view) return;

    if (view.sessionStorage.getItem(RELOAD_GUARD_KEY) === event.url) {
      this._clearGuard();
      return;
    }

    view.sessionStorage.setItem(RELOAD_GUARD_KEY, event.url);
    view.location.assign(this._location.prepareExternalUrl(event.url));
  }

  private _reloadForLazyChunk(): boolean {
    const view = this._document.defaultView;
    if (!view || view.navigator?.onLine === false) return false;

    const path = this._location.path();
    if (view.sessionStorage.getItem(LAZY_RELOAD_GUARD_KEY) === path) return false;

    view.sessionStorage.setItem(LAZY_RELOAD_GUARD_KEY, path);
    view.location.assign(this._location.prepareExternalUrl(path));
    return true;
  }

  private _isChunkLoadError(error: unknown): boolean {
    const message = typeof error === 'string' ? error : (error as { message?: unknown } | null)?.message;
    return typeof message === 'string' && CHUNK_LOAD_ERROR_PATTERN.test(message);
  }

  private _clearGuard(): void {
    this._document.defaultView?.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  }
}
